import { Box }                               from '@chakra-ui/react';
import { Text }                              from '@chakra-ui/react';
import React                                 from 'react';
import { observer }                          from 'mobx-react';
import { Link }                              from 'react-router-dom';
import { useTranslation }                    from 'react-i18next';
import { t }                                 from 'i18next';
import CodeModel                             from '@/models/services/code/CodeModel';
import CodePropertyValueModel                from '@/models/services/code/CodePropertyValueModel';
import DeliverableTransferTypeModel          from '@/models/services/shop/DeliverableTransferTypeModel';
import DeliverableTransferStatusHistoryModel from '@/models/services/shop/DeliverableTransferStatusHistoryModel';
import { Code_CodePropertyReference }        from '@/models/services/code/CodePropertyModel';
import Image                                 from '@/components/Image/Image';
import ActionsButton                         from '@/components/actionsButton/ActionsButton';
import { ModalData }                         from '@/components/modal/Modal';
import useTranslations                       from '@/hooks/useTranslations';
import { SupportedLanguage }                 from '@/hooks/useTranslations';
import useFetch                              from '@widesk-core/hooks/useFetch';
import useApiCollection                      from '@widesk-core/hooks/useApiCollection';
import { isTicketTransferable }              from '@/features/ticketTransfer/isTicketTransferable';
import { openTicketTransferModal }           from '@/features/ticketTransfer/openTicketTransferModal';
import FondationsEntertainmentModel          from '@/models/services/fondations/EntertainmentModel';
import { getIdFromUrn }                      from '@widesk-core/tools/identifier';
import ApiCollection                         from '@widesk-core/models/ApiCollection';
import CodeTypePropertyValueModel            from '@/models/services/code/CodeTypePropertyValueModel';
import { getVisualUrl }                      from '@/models/codeVisualUrl';

interface ITicketsListRowProps {
	code: CodeModel;
	transferStatusHistory?: DeliverableTransferStatusHistoryModel;
	codeTypePropertyValueCollection: ApiCollection<CodeTypePropertyValueModel>;
	setModalData: React.Dispatch<React.SetStateAction<ModalData | undefined>>;
	setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// Le visuel provient de la valeur de la propriété de type de code `visual_url` :
// produit Shop pour les tickets Shop, produit Gespass pour les codes Gespass.
const CodeImage = observer((props: {
	code: CodeModel;
	codeTypePropertyValueCollection: ApiCollection<CodeTypePropertyValueModel>;
}) => {
	const { code, codeTypePropertyValueCollection } = props;

	return (
		<Image
			errorSize="xl"
			borderRadius="md"
			sourceIsLoading={!codeTypePropertyValueCollection.isLoaded}
			src={getVisualUrl(code, codeTypePropertyValueCollection)}
		/>
	);
});

const TicketsListRow = (props: ITicketsListRowProps) => {
	const { code, transferStatusHistory, codeTypePropertyValueCollection, setModalData, setModalIsOpen } = props;

	const { i18n } = useTranslation();
	const { getNavigationTranslation } = useTranslations();
	const language = i18n.language as SupportedLanguage;

	const codePropertyValueCollection = useApiCollection(CodePropertyValueModel);
	const deliverableTransferTypeCollection = useApiCollection(DeliverableTransferTypeModel);

	const isGespass = code.isGespass;
	const codeValue = encodeURIComponent(code.value);

	const [ticketHasBeenTransfered, setTicketHasBeenTransfered] = React.useState(false);
	const [ownerEmail, setOwnerEmail] = React.useState('');

	const [gespassEntertainment, setGespassEntertainment] = React.useState<FondationsEntertainmentModel | undefined>();

	useFetch(async () => {

		const references: Code_CodePropertyReference[] = isGespass
			? ['firstname', 'lastname', 'email']
			: ['owner_firstname', 'owner_lastname', 'owner_birthdate', 'owner_email'];

		const result = await codePropertyValueCollection
			.setFilter('codeProperty.reference', references)
			.listBy(code.id, 'code')
			.then(coll => coll.whenIsLoaded(m => m.codeProperty));

		const owner = codePropertyValueCollection.find(cpv => cpv.codeProperty.reference === 'owner_email');
		setOwnerEmail(owner?.value || '');

		if (isGespass) {
			const entertainment = await new FondationsEntertainmentModel({
				id: getIdFromUrn(code.codeType.codeEntertainment.entertainmentUrn)
			}).fetch();

			setGespassEntertainment(entertainment);
		} else {
			await code.codeType.source.product.entertainment.whenIsLoaded();
		}

		return result;
	}, true);

	const transferTicket = () => {
		if (!code.deliverableUrn) return;

		openTicketTransferModal({
			codeKindReference: code.codeKind.reference,
			ownerEmail,
			deliverableUrn: code.deliverableUrn,
			deliverableTransferTypeCollection,
			setModalData,
			setModalIsOpen,
			onSuccess: () => {
				setTicketHasBeenTransfered(true);
			},
		});
	};

	const downloadTicket = async () => {
		await code.download();
	};

	const isDownloadable = code.isDownloadable;

	const isTransferable = isTicketTransferable({
		codeKindReference: code.codeKind.reference,
		ticketHasBeenTransfered,
		isBackedByDeliverable: code.isDeliverable || code.isDeliverableExchange,
		isFromShop: !!code?.isFromShop,
		transferStatusHistory,
	});

	const actions = [
		{
			label: t('pages.ticket.actions.download.label'),
			do: () => downloadTicket(),
			disabled: !isDownloadable,
		},
		{
			label: t('pages.ticket.actions.transfer.label'),
			do: () => transferTicket(),
			disabled: !isTransferable,
		},
	];

	return (
		<Box display="flex" alignItems="center" justifyContent="space-between">
			<Link
				to={`${getNavigationTranslation('ticket', 'path')}/${codeValue}`}
				style={{ width: '100%' }}
			>
				<Box display="flex" alignItems="center" gap="25px" width="100%">
					<Box maxWidth="65px" minWidth="65px" height="65px">
						<CodeImage code={code} codeTypePropertyValueCollection={codeTypePropertyValueCollection} />
					</Box>

					<Box display="flex" flexDirection="column" gap="5px">
						<Box>
							<Text className="poppins-semibold" fontSize="16px" textDecoration="underline">
								{code.getTitle(language)}
							</Text>
						</Box>
						<Box fontSize="12px">
							<Text>{code.value}</Text>
						</Box>
						<Box fontSize="12px">
							<Text>{code.isGespass ? gespassEntertainment?.getTitle(language) : code.codeType.source.product.entertainment.title}</Text>
						</Box>
					</Box>
				</Box>
			</Link>

			<ActionsButton actions={actions} />
		</Box>
	);
};

export default TicketsListRow;
